/**
 * FeeSplitModal — P26 (Pedro 2026-10-10), opened from Admin → RM commissions → Fee split.
 *
 * The agreement's FEE SPLIT: who is credited with how much of its billed fee, from a chosen
 * month. Decoupled from Named RMs — any active user may be in it (an RM, a referral, client
 * servicing). It must add up to 100%; until it does (or while someone in it has no commission
 * table) the monthly compute stops on this agreement — shown here in red. Earlier months keep
 * their own split; saving a month replaces that month's split.
 *
 * Commit model: the modal's own "Save split" (§3g Pattern B), like the Named RMs editor; the
 * dialog closes on a successful save.
 * Replaces the Commission-shares section that sat in the Named RMs editor (increment 2).
 *
 * Smoke 2026-10-11 (S12, Pedro): "From" defaults to the PAGE's month — the month the Monthly
 * tab computes — not the calendar month (a split saved from October was silently unused for
 * September); with no split yet, the first Named RM starts at 100% and the others at 0%.
 */
import {
  ActionIcon, Alert, Button, Group, Modal, NumberInput, Select, Stack, Table, Text, Tooltip,
} from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconUserPlus, IconX } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { ApiError } from '../api/client';
import { commissionsApi, type FeeSplitRow } from '../api/commissions';
import { useUsers } from '../hooks/useUsers';

const monthLabel = (iso: string): string => new Date(`${iso.slice(0, 7)}-01T00:00:00`)
  .toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });

interface Line { user_id: string; label: string; pct: number | '' }

export function FeeSplitModal({ row, month: pageMonth, onClose }: {
  row: FeeSplitRow;
  /** The page's month ('YYYY-MM-01'): the split in force is read for it and "From" starts on it. */
  month: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const usersQ = useUsers({ limit: 200 });
  const q = useQuery({
    queryKey: ['commissions', 'shares', row.agreement_id, pageMonth],
    queryFn: () => commissionsApi.shares(row.agreement_id, pageMonth),
  });
  const [month, setMonth] = useState<string>(pageMonth);
  const [lines, setLines] = useState<Line[] | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Start from the split in force; with none, from the named RMs: the first (primary first)
  // at 100%, the others at 0% (Pedro 2026-10-11) — the total is 100% and can be adjusted.
  useEffect(() => {
    if (!q.data || lines !== null) return;
    setLines(q.data.in_force.length
      ? q.data.in_force.map((s) => ({ user_id: s.user_id, label: s.user_label, pct: s.share_bps / 100 }))
      : q.data.named.map((r, i) => ({ user_id: r.user_id, label: r.user_label, pct: i === 0 ? 100 : 0 })));
  }, [q.data, lines]);

  const save = useMutation({
    mutationFn: () => commissionsApi.setShares(row.agreement_id, month,
      (lines ?? []).map((l) => ({ user_id: l.user_id, share_bps: Math.round(Number(l.pct) * 100) }))),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commissions'] });
      notifications.show({ color: 'green', message: `Fee split saved, from ${monthLabel(month)}` });
      setError(null);
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? e.detail : String(e)),
  });

  const current = lines ?? [];
  const inSplit = new Set(current.map((l) => l.user_id));
  const addOptions = (usersQ.data?.items ?? [])
    .filter((u) => !u.deleted_at && !inSplit.has(u.id))
    .map((u) => ({ value: u.id, label: `${u.display_name || u.email} (${u.role})` }));
  const total = current.reduce((s, l) => s + (l.pct === '' ? 0 : Number(l.pct)), 0);
  const allSet = current.length > 0 && current.every((l) => l.pct !== '');
  const totalOk = Math.abs(total - 100) < 0.005;
  const saveBlocked = !current.length ? 'Add at least one person'
    : !allSet ? 'Set a % for everyone in the split'
      : !totalOk ? 'The split must add up to 100%' : undefined;

  function add() {
    const u = (usersQ.data?.items ?? []).find((x) => x.id === picked);
    if (!u) return;
    // The first person in an empty split starts at 100% (Pedro 2026-10-11).
    setLines([...current, { user_id: u.id, label: u.display_name || u.email, pct: current.length ? '' : 100 }]);
    setPicked(null);
  }

  return (
    <Modal opened onClose={onClose} centered size="lg"
      title={<Text fw={600}>Fee split — {row.agreement_label}</Text>}>
      <Stack gap="sm">
        <Text size="xs" c="dimmed">
          Who is credited with how much of this agreement&apos;s billed fee, from the month chosen.
          Anyone can be in the split (an RM, a referral, client servicing). Earlier months keep
          the split that applied then.
          {row.named.length > 0 && ` Named RMs: ${row.named.map((r) => r.user_label).join(', ')}.`}
        </Text>
        {q.data?.problem && <Alert color="red" variant="light">{q.data.problem}</Alert>}
        <Group align="flex-end">
          <MonthPickerInput label="From" value={new Date(`${month}T00:00:00`)} w={160}
            onChange={(d) => d && setMonth(
              `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`)} />
          <Text size="xs" c="dimmed" mb={8}>
            Applies to {monthLabel(month)} and every later month until the next change.
            {month > pageMonth && ` Not to ${monthLabel(pageMonth)}, the month on the Monthly tab.`}
          </Text>
        </Group>
        <Table withTableBorder>
          <Table.Tbody>
            {current.map((l, i) => (
              <Table.Tr key={l.user_id}>
                <Table.Td><Text size="sm">{l.label}</Text></Table.Td>
                <Table.Td w={140}>
                  <NumberInput size="xs" min={0} max={100} decimalScale={2} suffix="%"
                    value={l.pct} placeholder="Not set" aria-label={`Share for ${l.label}`}
                    onChange={(v) => setLines(current.map((x, j) => (j === i
                      ? { ...x, pct: v === '' ? '' : Number(v) } : x)))} />
                </Table.Td>
                <Table.Td w={44}>
                  <Tooltip label="Remove from the split" withArrow>
                    <ActionIcon variant="subtle" color="red" aria-label={`Remove ${l.label}`}
                      onClick={() => setLines(current.filter((_, j) => j !== i))}>
                      <IconX size={14} />
                    </ActionIcon>
                  </Tooltip>
                </Table.Td>
              </Table.Tr>
            ))}
            <Table.Tr>
              <Table.Td><Text size="sm" fw={600}>Total</Text></Table.Td>
              <Table.Td>
                <Text size="sm" fw={600} c={totalOk ? 'green' : 'red'}>{total.toFixed(2)}%</Text>
              </Table.Td>
              <Table.Td />
            </Table.Tr>
          </Table.Tbody>
        </Table>
        <Group align="flex-end">
          <Select label="Add a person" placeholder="Pick a user…" data={addOptions} value={picked}
            onChange={setPicked} searchable clearable style={{ flex: 1 }}
            comboboxProps={{ withinPortal: true }} />
          <Button variant="light" leftSection={<IconUserPlus size={14} />}
            disabled={!picked} onClick={add}>Add</Button>
        </Group>
        {error && <Alert color="red">{error}</Alert>}
        <Group justify="flex-end">
          <Tooltip label={saveBlocked} disabled={!saveBlocked} withArrow>
            <span>
              <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!!saveBlocked}>
                Save split
              </Button>
            </span>
          </Tooltip>
        </Group>
        {(q.data?.history.length ?? 0) > 0 && (
          <Stack gap={2}>
            <Text size="xs" fw={600}>History</Text>
            {q.data?.history.map((h) => (
              <Text key={h.effective_from} size="xs" c="dimmed">
                From {monthLabel(h.effective_from)}:{' '}
                {h.shares.map((s) => `${s.user_label} ${s.share_bps / 100}%`).join(' · ')}
              </Text>
            ))}
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
